import type { ProcHistoryEntry, ProcListKey, ProcRow } from '@type/store';

export const PROC_STATUS = {
  YET: 'Yet to Initiate',
  IN_PROCESS: 'In Process',
  ON_GOING: 'On Going',
  COMPLETED: 'Completed',
} as const;

export type ProcStatusValue = typeof PROC_STATUS[keyof typeof PROC_STATUS];

export interface ProcRegisterState {
  procYetToInitiate?: ProcRow[];
  procInprocess?: ProcRow[];
  procOngoing?: ProcRow[];
  procCompleted?: ProcRow[];
  procCivil?: ProcRow[];
}

export interface ProcListIndexResult {
  key: ProcListKey | null;
  index: number;
}

export function procCmCategory(type: unknown): 'coe' | 'cons' | 'nc' | null {
  if (type === 'Goods') return 'coe';
  if (type === 'Consultancy') return 'cons';
  if (type === 'Non-Consultancy') return 'nc';
  return null;
}

export function normalizeProcRow(row: ProcRow): ProcRow {
  return {
    ...row,
    id: row.id || row.ref || `proc-${Date.now()}`,
    ref: row.ref || '',
    name: row.name || '',
    type: row.type || 'Goods',
    fy: row.fy || '2025-26',
    party: row.party || '',
    pkr: Number(row.pkr) || 0,
    usd: row.usd ?? '',
    status: row.status || PROC_STATUS.YET,
    dateOfEntry: row.dateOfEntry || '',
    remarks: row.remarks || '',
    history: Array.isArray(row.history) ? row.history : [],
    advertisedOn: row.advertisedOn || '',
    ppRef: row.ppRef || '',
    bidOpenDate: row.bidOpenDate || '',
    bidValidityDays: row.bidValidityDays ?? '',
    bidValidityTill: row.bidValidityTill || '',
    firmsParticipated: row.firmsParticipated || '',
    shortlistedGroup: row.shortlistedGroup || '',
    technicallyQualified: row.technicallyQualified || '',
    bidByQualifiedFirm: row.bidByQualifiedFirm ?? '',
    qualifiedFirmName: row.qualifiedFirmName || '',
    contractSignDate: row.contractSignDate || '',
    currentStatus: row.currentStatus || row.status || '',
    procRemarks: row.procRemarks || '',
    procId: row.procId || row.id || '',
  };
}

export function appendProcHistory(row: ProcRow, entry: Record<string, unknown>): ProcRow {
  const history = [...(Array.isArray(row.history) ? row.history as ProcHistoryEntry[] : []), {
    at: new Date().toLocaleString(),
    ...entry,
  }];
  return { ...row, history };
}

export const PROC_TYPE_TABS = ['Goods', 'Consultancy', 'Non-Consultancy', 'Civil Works'] as const;
export type ProcTypeTab = typeof PROC_TYPE_TABS[number];

export function isProcTypeTab(tab: string): tab is ProcTypeTab {
  return (PROC_TYPE_TABS as readonly string[]).includes(tab);
}

export function filterProcByTab(rows: ProcRow[], tab: string, types: string[] = []): ProcRow[] {
  const data = rows || [];
  if (isProcTypeTab(tab)) {
    return data.filter((r) => String(r.type ?? '') === tab);
  }
  let filtered = data;
  if (types.length) {
    filtered = filtered.filter((r) => types.includes(String(r.type ?? '')));
  }
  if (tab === 'all') return filtered;
  if (tab === 'Yet to Initiate') return filtered.filter((r) => r.status === PROC_STATUS.YET);
  if (tab === 'In Process') return filtered.filter((r) => r.status === PROC_STATUS.IN_PROCESS);
  if (tab === 'On Going') return filtered.filter((r) => r.status === PROC_STATUS.ON_GOING);
  if (tab === 'Completed') return filtered.filter((r) => r.status === PROC_STATUS.COMPLETED);
  return filtered;
}

export function filterProcRegisterView(
  rows: ProcRow[],
  tab: string,
  typeFilters: string[],
  search = '',
  yearFilter = '',
  monthFilter = '',
): ProcRow[] {
  let data = filterProcByTab(rows, tab, isProcTypeTab(tab) ? [] : typeFilters);
  if (isProcTypeTab(tab)) {
    data = data.filter((r) => String(r.type ?? '') === tab);
  }

  const q = search.trim().toLowerCase();
  if (q) {
    data = data.filter((r) => `${r.name}${r.ref}${r.party || ''}`.toLowerCase().includes(q));
  }
  if (yearFilter) {
    data = data.filter((r) => {
      const fy = String(r.fy || '');
      if (fy.startsWith(yearFilter)) return true;
      const dateStr = String(r.dateOfEntry || r.contractSignDate || r.advertisedOn || '');
      const y = parseProcYearFromDate(dateStr);
      return y != null && String(y) === yearFilter;
    });
  }
  if (monthFilter) {
    data = data.filter((r) => {
      const dateStr = String(r.dateOfEntry || r.contractSignDate || r.advertisedOn || '');
      const m = parseProcMonthFromDate(dateStr);
      return m != null && String(m) === monthFilter;
    });
  }
  if (tab === 'In Process') {
    data = data.filter((r) => r.ref);
  }
  return data;
}

function parseProcYearFromDate(dateStr: string): number | null {
  if (!dateStr) return null;
  const iso = new Date(dateStr);
  if (!Number.isNaN(iso.getTime())) return iso.getFullYear();
  const dot = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dot) return +dot[3];
  const slash = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) return +slash[3];
  return null;
}

function parseProcMonthFromDate(dateStr: string): number | null {
  if (!dateStr) return null;
  const iso = new Date(dateStr);
  if (!Number.isNaN(iso.getTime())) return iso.getMonth() + 1;
  const dot = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dot) return +dot[2];
  const slash = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) return +slash[2];
  return null;
}

export function procurementYearOptions(rows: ProcRow[]): string[] {
  const years = new Set<string>();
  rows.forEach((r) => {
    const fy = String(r.fy || '');
    const match = fy.match(/^(\d{4})/);
    if (match) years.add(match[1]);
    const y = parseProcYearFromDate(String(r.dateOfEntry || r.contractSignDate || ''));
    if (y) years.add(String(y));
  });
  return [...years].sort((a, b) => b.localeCompare(a));
}

export function procRowKey(row: ProcRow, index: number, viewKey = ''): string {
  return `${viewKey}:${row.id ?? row.ref ?? 'row'}:${index}`;
}

export function mergeProcRegister(state: ProcRegisterState): ProcRow[] {
  const {
    procYetToInitiate = [],
    procInprocess = [],
    procOngoing = [],
    procCompleted = [],
    procCivil = [],
  } = state;
  return [
    ...procYetToInitiate.map(normalizeProcRow),
    ...procInprocess.map((r) => normalizeProcRow({ ...r, status: r.status || PROC_STATUS.IN_PROCESS })),
    ...procOngoing.map((r) => normalizeProcRow({ ...r, status: r.status || PROC_STATUS.ON_GOING })),
    ...procCompleted.map((r) => normalizeProcRow({ ...r, status: r.status || PROC_STATUS.COMPLETED })),
    ...procCivil.map((r) => normalizeProcRow({ ...r, status: r.status || PROC_STATUS.ON_GOING, type: 'Civil Works' })),
  ];
}

export function procHistoryDesignation(entry: ProcHistoryEntry): string {
  const designated = entry.byDesignation as string | undefined;
  if (designated?.trim()) return designated;
  const role = String(entry.byRole || '').trim();
  const roleLabels: Record<string, string> = {
    procurement: 'Deputy Director Procurement & Contracts',
    proc_dd: 'Deputy Director (Equipment)',
    proc_ad: 'Assistant Director (ETS)',
    proc_dir: 'Director Training & Equipment',
    contractmgmt: 'Contract Management Officer',
  };
  if (role && roleLabels[role]) return roleLabels[role];
  if (role) return role;
  return String(entry.by || '—');
}

export function procHistoryActor(actor?: { name?: string; role?: string; roleLabel?: string } | null) {
  const by = actor?.name || 'User';
  const byRole = actor?.role || '';
  const byDesignation = actor?.roleLabel || procHistoryDesignation({ by, byRole });
  return { by, byRole, byDesignation };
}

export function resolveProcRowFromHistory(
  state: ProcRegisterState,
  entry: ProcHistoryEntry,
): { row: ProcRow; key: ProcListKey | null; index: number } {
  const probe = { id: entry.packageId, ref: entry.ref };
  const found = findProcListIndex(state, probe as ProcRow);
  if (found.key && found.index >= 0) {
    const list = state[found.key] || [];
    return { row: normalizeProcRow(list[found.index]), key: found.key, index: found.index };
  }
  return { row: {}, key: null, index: -1 };
}

export function packageActivityHistory(row: ProcRow | null | undefined): ProcHistoryEntry[] {
  return Array.isArray(row?.history) ? row.history as ProcHistoryEntry[] : [];
}

export const PROCUREMENT_OFFICER_TABS = [
  { id: 'all', label: 'All Packages' },
  { id: 'In Process', label: 'In Process' },
  { id: 'Completed', label: 'Completed Procurement' },
  { id: 'Goods', label: 'Goods' },
  { id: 'Consultancy', label: 'Consultancy' },
  { id: 'Non-Consultancy', label: 'Non-Consultancy' },
  { id: 'Civil Works', label: 'Civil Works' },
] as const;

export const PROCUREMENT_PNC_TABS = PROCUREMENT_OFFICER_TABS.filter((t) => t.id !== 'Civil Works');

export const PROCUREMENT_TYPE_FILTER_OPTIONS = [
  { id: 'Goods', label: 'Goods' },
  { id: 'Consultancy', label: 'Consultancies' },
  { id: 'Non-Consultancy', label: 'Non-Consultancies' },
] as const;

export const PROCUREMENT_TYPE_OPTIONS = ['Goods', 'Consultancy', 'Non-Consultancy', 'Civil Works'] as const;

export const PROCUREMENT_PNC_TYPE_OPTIONS = PROCUREMENT_TYPE_OPTIONS.filter((t) => t !== 'Civil Works');

export function procurementTabsForRole(role?: string) {
  return role === 'procurement' ? PROCUREMENT_PNC_TABS : PROCUREMENT_OFFICER_TABS;
}

export function procurementTypeOptionsForRole(role?: string) {
  return role === 'procurement' ? [...PROCUREMENT_PNC_TYPE_OPTIONS] : [...PROCUREMENT_TYPE_OPTIONS];
}

export function excludeCivilWorksForPnc(rows: ProcRow[], role?: string) {
  if (role !== 'procurement') return rows;
  return rows.filter((r) => String(r.type ?? '') !== 'Civil Works');
}

export function procHistoryStats(history: ProcHistoryEntry[] | undefined, role?: string) {
  const items = (history || []).filter((h) => !role || h.byRole === role || h.by?.includes(role));
  return {
    total: items.length,
    inProcess: items.filter((h) => h.to === PROC_STATUS.IN_PROCESS).length,
    onGoing: items.filter((h) => h.to === PROC_STATUS.ON_GOING).length,
    completed: items.filter((h) => h.to === PROC_STATUS.COMPLETED).length,
  };
}

export function findProcListIndex(state: ProcRegisterState, row: ProcRow | null | undefined): ProcListIndexResult {
  if (!row) return { key: null, index: -1 };
  const matches = (r: ProcRow) => (row.id && r.id === row.id) || (row.ref && r.ref === row.ref);
  const lists: [ProcListKey, string][] = [
    ['procCompleted', PROC_STATUS.COMPLETED],
    ['procOngoing', PROC_STATUS.ON_GOING],
    ['procInprocess', PROC_STATUS.IN_PROCESS],
    ['procYetToInitiate', PROC_STATUS.YET],
  ];
  for (const [key] of lists) {
    const index = (state[key] || []).findIndex(matches);
    if (index >= 0) return { key, index };
  }
  if (row.type === 'Civil Works') {
    const index = (state.procCivil || []).findIndex(matches);
    if (index >= 0) return { key: 'procCivil', index };
  }
  return { key: null, index: -1 };
}

export function resolveProcStoreRow(
  state: ProcRegisterState,
  row?: ProcRow | null,
  index?: number | null,
  listKey?: ProcListKey | null,
): { row: ProcRow; index: number; key: ProcListKey | null } {
  if (listKey && typeof index === 'number' && index >= 0) {
    const list = state[listKey] || [];
    const at = list[index];
    if (at && ((!row?.id && !row?.ref) || (row.id && at.id === row.id) || (row.ref && at.ref === row.ref))) {
      return { row: normalizeProcRow(at), index, key: listKey };
    }
  }
  const found = findProcListIndex(state, row);
  if (found.key && found.index >= 0) {
    const list = state[found.key] || [];
    return { row: normalizeProcRow(list[found.index]), index: found.index, key: found.key };
  }
  return { row: normalizeProcRow(row || {}), index: -1, key: null };
}

export function findCmRowForProc(
  contractMgmt: Record<string, ProcRow[]>,
  procRow: ProcRow | null | undefined,
): { cat: 'coe' | 'piu' | 'cons' | 'nc'; row: ProcRow } | null {
  if (!procRow) return null;
  const probe: ProcRow = { procId: procRow.id, procRef: procRow.ref, name: procRow.name };
  const typeCat = procCmCategory(procRow.type);
  const searchOrder: Array<'coe' | 'piu' | 'cons' | 'nc'> = typeCat === 'coe'
    ? ['coe', 'piu']
    : typeCat === 'cons'
      ? ['cons']
      : typeCat === 'nc'
        ? ['nc']
        : ['coe', 'piu', 'cons', 'nc'];
  for (const cat of searchOrder) {
    const list = contractMgmt[cat] || [];
    const idx = findCmRowIndex(list, probe);
    if (idx >= 0) return { cat, row: list[idx] };
  }
  return null;
}

export function resolveCmLinkForProc(
  contractMgmt: Record<string, ProcRow[]>,
  procRow: ProcRow | null | undefined,
): { cat: 'coe' | 'piu' | 'cons' | 'nc'; row: ProcRow } | null {
  const live = findCmRowForProc(contractMgmt, procRow);
  if (live) return live;
  const snap = procRow?.cmData as { cat?: string; row?: ProcRow } | undefined;
  if (snap?.cat && snap?.row) {
    return { cat: snap.cat as 'coe' | 'piu' | 'cons' | 'nc', row: snap.row };
  }
  return null;
}

export function isProcGoodsType(type: unknown) {
  return type === 'Goods';
}

export function isProcServicesType(type: unknown) {
  return type === 'Consultancy' || type === 'Non-Consultancy';
}

export function buildCmRowFromProc(procRow: ProcRow): ProcRow | null {
  const cat = procCmCategory(procRow.type);
  const base: ProcRow = {
    sr: 0,
    name: procRow.name,
    status: 'On Going',
    delivery: 'Pending',
    payment: 'Pending',
    remarks: procRow.remarks || '',
    procId: procRow.id,
    procRef: procRow.ref,
  };
  if (cat === 'coe') {
    return {
      ...base,
      amt: procRow.pkr ? `PKR ${Number(procRow.pkr).toLocaleString()}` : (procRow.usd ? `USD ${procRow.usd}` : '—'),
      vendor: procRow.qualifiedFirmName || procRow.party || '—',
      contract: procRow.contractSignDate || '—',
      po: procRow.ppRef || '—',
      warranty: '',
      inspection: 'Pending',
      perfSecurity: '',
      perfSecurityType: '',
      perfSecurityRemarks: '',
      deliverySchedule: '',
    };
  }
  if (cat === 'cons') {
    return {
      ...base,
      pkr: procRow.pkr || 0,
      usd: procRow.usd || '',
      firm: procRow.qualifiedFirmName || procRow.party || '—',
      contract: procRow.contractSignDate || '—',
      comm: procRow.contractSignDate || procRow.advertisedOn || '—',
      insurance: 'Valid',
      deliverables: [],
      totalRemuneration: '',
      totalReimbursable: '',
      paidAmount: '',
      paidRemuneration: '',
      paidReimbursable: '',
      paymentPct: '',
      paymentPctRemarks: '',
    };
  }
  if (cat === 'nc') {
    return {
      ...base,
      pkr: procRow.pkr || 0,
      firm: procRow.qualifiedFirmName || procRow.party || '—',
      contract: procRow.contractSignDate || '—',
      comm: procRow.contractSignDate || procRow.advertisedOn || '—',
      insurance: 'Valid',
      deliverables: [],
      totalRemuneration: '',
      totalReimbursable: '',
      paidAmount: '',
      paidRemuneration: '',
      paidReimbursable: '',
      paymentPct: '',
      paymentPctRemarks: '',
    };
  }
  return null;
}

export function findCmRowIndex(rows: ProcRow[], row: ProcRow | null | undefined): number {
  if (!row || !Array.isArray(rows)) return -1;
  if (row.procId) {
    const byProc = rows.findIndex((r) => r.procId === row.procId);
    if (byProc >= 0) return byProc;
  }
  if (row.procRef) {
    const byRef = rows.findIndex((r) => r.procRef === row.procRef);
    if (byRef >= 0) return byRef;
  }
  return rows.findIndex((r) => r.sr === row.sr && r.name === row.name);
}

export function mergeCmRow(prev: ProcRow | null | undefined, updates: ProcRow | null | undefined): ProcRow {
  const clean: ProcRow = { ...(prev || {}) };
  const patch: ProcRow = { ...(updates || {}) };
  delete patch._idx;
  delete patch._origIdx;
  Object.entries(patch).forEach(([key, val]) => {
    if (val !== undefined) clean[key] = val;
  });
  return clean;
}

export function stripCmRowMeta(row: ProcRow | null | undefined): ProcRow {
  if (!row) return {};
  const { _idx, _origIdx, ...rest } = row as ProcRow & { _idx?: number; _origIdx?: number };
  return rest;
}
